import { useCallback } from 'react'

/**
 * Keyboard navigation constants and utilities for consistent behavior
 * across MRT tables, dialogs, and other interactive components.
 */

// Keys that should be blocked from propagating to parent components (e.g., MRT table navigation)
// when user is actively editing text in an input/textarea field
export const TEXT_EDITING_KEYS = [
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'ArrowDown',
	'Home',
	'End',
	'PageUp',
	'PageDown',
] as const

// Keys used for cell-to-cell navigation in tables (when NOT in edit mode)
export const TABLE_NAVIGATION_KEYS = [
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'ArrowDown',
	'Home',
	'End',
	'Tab',
] as const

// Keys for modal/dialog interactions
export const MODAL_KEYS = {
	CLOSE: 'Escape',
	SUBMIT: 'Enter', // with Ctrl/Cmd modifier
} as const

// Editable element tag names
const EDITABLE_TAGS = ['input', 'textarea'] as const

type TextEditingKey = (typeof TEXT_EDITING_KEYS)[number]
type TableNavigationKey = (typeof TABLE_NAVIGATION_KEYS)[number]

/**
 * Check if the event target is an editable element (input, textarea, or contenteditable)
 */
export const isEditableElement = (target: EventTarget | null): boolean => {
	if (!target || !(target instanceof HTMLElement)) return false

	const tagName = target.tagName.toLowerCase()
	if (EDITABLE_TAGS.includes(tagName as (typeof EDITABLE_TAGS)[number])) {
		return true
	}

	// Check for contenteditable elements
	return target.isContentEditable
}

/**
 * Check if the key is a text editing navigation key
 */
export const isTextEditingKey = (key: string): key is TextEditingKey => {
	return TEXT_EDITING_KEYS.includes(key as TextEditingKey)
}

/**
 * Check if the key is a table navigation key
 */
export const isTableNavigationKey = (key: string): key is TableNavigationKey => {
	return TABLE_NAVIGATION_KEYS.includes(key as TableNavigationKey)
}

/**
 * Check if the event has a modifier key pressed (Ctrl, Cmd, Alt, Shift)
 */
export const hasModifier = (event: KeyboardEvent | React.KeyboardEvent): boolean => {
	return event.ctrlKey || event.metaKey || event.altKey || event.shiftKey
}

/**
 * Check if the save shortcut is pressed (Ctrl+Enter or Cmd+Enter)
 */
export const isSaveShortcut = (event: KeyboardEvent | React.KeyboardEvent): boolean => {
	return (event.ctrlKey || event.metaKey) && event.key === 'Enter'
}

/**
 * Check if the close shortcut is pressed (Escape)
 */
export const isCloseShortcut = (event: KeyboardEvent | React.KeyboardEvent): boolean => {
	return event.key === MODAL_KEYS.CLOSE
}

interface UseKeyboardNavigationOptions {
	/** Whether the component is in edit mode */
	isEditing?: boolean
	/** Callback when close/cancel shortcut is triggered */
	onClose?: () => void,
	/** Callback when save shortcut is triggered */
	onSave?: () => void
}

interface KeyboardNavigationHandlers {
	/** Handler for cell containers - manages navigation vs editing */
	handleCellKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void,
	/** Handler for modal/dialog content */
	handleModalKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void,
	/** Handler for text input/textarea elements - stops propagation for editing keys */
	handleTextInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

/**
 * Hook providing consistent keyboard navigation handlers for MRT tables and dialogs.
 *
 * @example
 * // In a table cell component
 * const { handleTextInputKeyDown } = useKeyboardNavigation()
 * <TextField onKeyDown={handleTextInputKeyDown} />
 *
 * @example
 * // In a modal with save/close functionality
 * const { handleModalKeyDown } = useKeyboardNavigation({
 *   onSave: handleSave,
 *   onClose: handleClose,
 * })
 * <div onKeyDown={handleModalKeyDown}>...</div>
 */
export const useKeyboardNavigation = (
	options: UseKeyboardNavigationOptions = {},
): KeyboardNavigationHandlers => {
	const { onSave, onClose } = options

	/**
	 * Handler for text input/textarea elements.
	 * Stops propagation of navigation keys to allow normal text editing.
	 */
	const handleTextInputKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			// Allow text editing navigation keys to work normally in inputs
			if (isTextEditingKey(event.key)) {
				event.stopPropagation()
			}
		},
		[],
	)

	/**
	 * Handler for table cell containers.
	 * When in an editable element, prevents navigation keys from bubbling to MRT.
	 */
	const handleCellKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			const target = event.target as HTMLElement

			// If we're in an editable element, stop navigation keys from propagating
			if (isEditableElement(target) && isTextEditingKey(event.key)) {
				event.stopPropagation()
			}
		},
		[],
	)

	/**
	 * Handler for modal/dialog content.
	 * Provides keyboard shortcuts and prevents event leaking.
	 */
	const handleModalKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLElement>) => {
			// Handle save shortcut (Ctrl/Cmd + Enter)
			if (isSaveShortcut(event) && onSave) {
				event.preventDefault()
				event.stopPropagation()
				onSave()
				return
			}

			// Handle close shortcut (Escape)
			if (isCloseShortcut(event) && onClose) {
				event.stopPropagation()
				onClose()
				return
			}

			// If we're in an editable element, stop navigation keys from propagating
			// This prevents MRT table navigation when typing in modal inputs
			if (isEditableElement(event.target) && isTextEditingKey(event.key)) {
				event.stopPropagation()
			}
		},
		[onSave, onClose],
	)

	return {
		handleTextInputKeyDown,
		handleCellKeyDown,
		handleModalKeyDown,
	}
}

/**
 * Standalone utility function for handling keyboard events in text inputs.
 * Can be used without the hook for simpler cases.
 */
export const stopNavigationKeyPropagation = (
	event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
): void => {
	if (isTextEditingKey(event.key)) {
		event.stopPropagation()
	}
}
